import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  shopifyKeys,
  useShopifySalesExport,
  useShopifyConversionsPage,
  useShopifyConversionsSeries,
  useShopifyConversionsSummary,
} from '@discovr/features/shopify';
import type {
  ShopifyCampaignCouponRow,
  ShopifyConversionScope,
  ShopifyConversionSortKey,
  ShopifyConversionsParams,
  ShopifyConversionsRange,
  ShopifyConversionsSummary,
  ShopifyCouponMetrics,
  ShopifyCreatorCouponRow,
} from '@discovr/types';
import { LoadingSpinner } from '../components/common/LoadingSpinner';
import {
  ArrowsClockwise,
  DownloadSimple,
  ShoppingBag,
  Receipt,
  ArrowUUpLeft,
  CursorClick,
  Percent,
  WarningCircle,
  type Icon,
} from '@phosphor-icons/react';
import { EmptyState } from '../components/common/EmptyState';
import { KpiCard } from '../components/common/KpiCard';
import { DataTable } from '../components/common/DataTable';
import type { ColumnDef } from '../components/common/DataTable';
import { TruncatedText } from '../components/common/TruncatedText';
import { InfoTooltip } from './InfoTooltip';
import { formatStoreMoney } from '../campaign-detail/components/shopifyCurrency';
import { DISPLAY_DECIMALS } from '../lib/format';
import { shopifyOrdersUrl } from '../campaign-detail/components/shopifyOrdersUrl';
import { ApiError } from '@discovr/common';
import { Alert } from '../components/ui/alert';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { SimpleTooltip } from '../components/ui/tooltip';
import { cn } from '../lib/utils';
import { useBelowMd } from '../lib/useBelowMd';
import { shopifyCopy } from '@discovr/features/copy';
import { useCampaignCapabilitiesStore } from '@discovr/features';
import { ShopifyDataRemovedNotice } from '../integrations/ShopifyDataRemovedNotice';
import {
  ShopifyCodeCell,
  CouponStatusBadge,
} from '../campaign-detail/components/shopifyCouponCells';
import {
  RangeControl,
  SalesTrendChart,
  rangeForPreset,
  type RangePreset,
} from './ShopifyConversionsTrend';
import {
  ShopifyRecordedActivity,
  ShopifyRefreshFailedNotice,
  TableRefreshFailedNotice,
} from './ShopifyReportStatus';
import { isDeletingStoreData, unavailableStoreCopy } from './shopifyReportState';
import { ShopifyHelpLink, ShopifySetupStrip } from './ShopifySetupStrip';
import { ShopifyRefusedOrders } from './ShopifyRefusedOrders';
import { useShopifyCampaignNav } from '../campaign-detail/components/useShopifyCampaignNav';
import { PhaseSubTabs } from '../campaign-detail/components/parts/PhaseSubTabs';
import { PAGE_SIZE_OPTIONS } from '../campaign-detail/useCampaignPageSizeParam';
import {
  PAGED_TABLE_MAX_SIZE,
  usePagedTableUrlState,
  useResetPages,
  useSnapPastEnd,
} from '../campaign-detail/pagedTableState';

interface ShopifyConversionsTabProps {
  campaignId?: string;
  /** Reports whether Shopify is relevant to this campaign, once known
   *  (undefined while loading). See ConversionsTab's onConnectionChange. */
  onConnectionChange?: (connected: boolean) => void;
  /** Whether Shopify is turned on for this campaign; undefined while unknown. */
  shopifyEnabled?: boolean;
}

const NO_SALES_YET =
  'No sales yet. Discount-code sales usually appear within a couple of minutes of checkout. Sales through a creator’s link without a code can take up to 48 hours, because Shopify provides that data later, and some can’t be credited when the shopper’s browser blocks tracking.';

const intFmt = new Intl.NumberFormat(undefined);
const fmtInt = (n: number): string => intFmt.format(Math.round(n ?? 0));
const plainMoneyFmt = new Intl.NumberFormat(undefined, {
  minimumFractionDigits: DISPLAY_DECIMALS,
  maximumFractionDigits: DISPLAY_DECIMALS,
});

/** Currency formatter for the shop's currency; falls back to a plain 2-decimal
 *  number when the BE hasn't reported one (the BE defaults to INR, so this is rare). */
function makeMoneyFmt(currency?: string): (n: number) => string {
  if (!currency) return (n) => plainMoneyFmt.format(n ?? 0);
  return (n) => formatStoreMoney(n ?? 0, currency);
}

/** Creator display label — name, else handle, else the code (BE resolves
 *  identity live from the roster; it's absent for an off-roster creator). */
function creatorLabel(c: ShopifyCreatorCouponRow): string {
  return c.display_name || c.handle || c.code;
}

/** The BE field is `clicks_directional`: tagged page views the pixel recorded,
 *  repeats included, so the figure is approximate visits, not clicks or people. */
const { visits: VISITS, paidOrders: PAID_ORDERS, visitRate: VISIT_RATE } = shopifyCopy.report;

/** Header hint sits beside the sort button, so opening it never changes the sort. */
function HintIcon({ label, hint }: { label: string; hint: string }) {
  return <InfoTooltip label={`About the ${label} column`}>{hint}</InfoTooltip>;
}

const visitsHeaderProps = {
  header: VISITS.header,
  sortLabel: VISITS.header,
  headerAddon: <HintIcon label={VISITS.header} hint={VISITS.hint} />,
};

const paidOrdersHeaderProps = {
  header: PAID_ORDERS.label,
  sortLabel: PAID_ORDERS.label,
  headerAddon: <HintIcon label={PAID_ORDERS.label} hint={PAID_ORDERS.hint} />,
};

const { label: NET_SALES_HEADER, hint: NET_SALES_HINT } = shopifyCopy.report.netSales;
const AWAITING_LABEL = 'Awaiting payment';
const AWAITING_HINT =
  'Pending or authorized orders placed with these codes. They move into net sales once paid.';
const PARTIALLY_PAID_LABEL = 'Partially paid';
const PARTIALLY_PAID_HINT =
  'Orders with part of the payment received. They move into net sales once fully paid.';

const plural = (n: number, one: string, many: string): string =>
  `${fmtInt(n)} ${n === 1 ? one : many}`;

function NetSalesCell({
  row,
  fmtMoney,
}: {
  row: ShopifyCouponMetrics;
  fmtMoney: (n: number) => string;
}) {
  const unknown = row.unknown_payment_orders ?? 0;
  return (
    <div className="flex flex-col items-end">
      <span className="text-foreground font-semibold">{fmtMoney(row.net_gmv)}</span>
      {unknown > 0 ? (
        <span className="text-muted-foreground text-caption-regular">
          +{plural(unknown, 'order', 'orders')} not counted
        </span>
      ) : null}
    </div>
  );
}

const linkOrdersHint = (n: number): string => `${fmtInt(n)} via link (no code used)`;

function CreatorOrdersCell({ row }: { row: ShopifyCouponMetrics }) {
  const linkOrders = row.link_orders ?? 0;
  if (!(linkOrders > 0)) return <span className="text-foreground">{fmtInt(row.orders)}</span>;
  return (
    <SimpleTooltip tapToToggle content={linkOrdersHint(linkOrders)}>
      <span
        tabIndex={0}
        className="text-foreground focus-visible:outline-ring focus-visible:outline-2"
      >
        {fmtInt(row.orders)}
      </span>
    </SimpleTooltip>
  );
}

function PaymentBucketCell({
  gmv,
  orders,
  fmtMoney,
}: {
  gmv: number | undefined;
  orders: number | undefined;
  fmtMoney: (n: number) => string;
}) {
  if (!((orders ?? 0) > 0)) return <span className="text-muted-foreground">{fmtMoney(0)}</span>;
  return (
    <div className="flex flex-col items-end">
      <span className="text-foreground">{fmtMoney(gmv ?? 0)}</span>
      <span className="text-muted-foreground text-caption-regular">
        {plural(orders ?? 0, 'order', 'orders')}
      </span>
    </div>
  );
}

function netSalesColumn<T extends ShopifyCouponMetrics>(
  fmtMoney: (n: number) => string,
): ColumnDef<T> {
  return {
    id: 'net_gmv',
    header: NET_SALES_HEADER,
    sortLabel: NET_SALES_HEADER,
    headerAddon: <HintIcon label={NET_SALES_HEADER} hint={NET_SALES_HINT} />,
    minWidth: 96,
    align: 'right',
    sortable: true,
    cell: (r) => <NetSalesCell row={r} fmtMoney={fmtMoney} />,
  };
}

/** Test orders stay in every count; the pill only labels them. */
function TestOrdersPill({ count }: { count: number }) {
  return (
    <SimpleTooltip
      tapToToggle
      content={`${plural(count, 'test order', 'test orders')} placed in Shopify test mode. Never billed.`}
    >
      <Badge variant="secondary" tabIndex={0}>
        Test
      </Badge>
    </SimpleTooltip>
  );
}

/** The code with its status badge underneath; there is no Status column. */
function CodeCell({ row }: { row: ShopifyCouponMetrics }) {
  const testOrders = row.test_orders ?? 0;
  const canOpenShopify = useCampaignCapabilitiesStore(
    (s) => s.lane === 'brand' && (s.accessLevel === 'write' || s.accessLevel === 'admin'),
  );
  const ordersUrl = canOpenShopify ? shopifyOrdersUrl(row.shop_domain, row.code) : null;
  return (
    <div className="flex min-w-0 flex-col items-start gap-1">
      <div className="flex max-w-full min-w-0 items-center gap-1.5">
        <ShopifyCodeCell code={row.code} className="text-muted-foreground text-caption-regular" />
        {testOrders > 0 ? <TestOrdersPill count={testOrders} /> : null}
      </div>
      <CouponStatusBadge
        status={row.status}
        shopifyState={row.shopify_state}
        usageLimitReached={row.usage_limit_reached}
      />
      {ordersUrl ? (
        <Button asChild variant="link" className="h-auto p-0 text-caption-regular">
          <a href={ordersUrl} target="_blank" rel="noopener noreferrer">
            View orders in Shopify
            <span className="sr-only"> (opens in a new tab)</span>
          </a>
        </Button>
      ) : null}
    </div>
  );
}

/** The tiles cover every code; when inactive ones carry sales, the Active
 *  table alone won't add up to them, so say where the rest is. */
function InactiveSalesNote({ sum, fmtMoney }: { sum: number; fmtMoney: (n: number) => string }) {
  if (!(sum > 0)) return null;
  return (
    <p className="text-muted-foreground text-caption-regular">Includes {fmtMoney(sum)} from inactive codes</p>
  );
}

/** Sales on inactive codes. The summary has no such total, so read the inactive
 *  pages of only the scopes that have inactive codes. */
function useInactiveSales(
  campaignId: string,
  creatorRevoked: number,
  campaignRevoked: number,
  range: ShopifyConversionsRange,
) {
  const page = (scope: ShopifyConversionScope) => ({
    scope,
    revoked: true,
    page: 1,
    limit: PAGED_TABLE_MAX_SIZE,
    ...range,
  });
  const creators = useShopifyConversionsPage(campaignId, page('creator'), creatorRevoked > 0);
  const campaigns = useShopifyConversionsPage(campaignId, page('campaign'), campaignRevoked > 0);
  const entries = [...(creators.data?.entries ?? []), ...(campaigns.data?.entries ?? [])];
  return {
    gmv: entries.reduce((acc, r) => acc + r.net_gmv, 0),
    orders: entries.reduce((acc, r) => acc + r.orders, 0),
  };
}

const UNPAID_LABEL = 'Unpaid';
const UNPAID_HINT = `${AWAITING_LABEL}: ${AWAITING_HINT} ${PARTIALLY_PAID_LABEL}: ${PARTIALLY_PAID_HINT}`;

const unpaidOrders = (r: ShopifyCouponMetrics): number =>
  (r.awaiting_payment_orders ?? 0) + (r.partially_paid_orders ?? 0);

/** The Unpaid column shows only when some row in the table has unpaid orders. */
interface BucketVisibility {
  unpaid: boolean;
  /** Some row carries a ROAS (the API omits it when it can't compute one). */
  roas: boolean;
}

const rowRoas = (r: ShopifyCouponMetrics): number | undefined => (r as { roas?: number }).roas;

function bucketVisibility(rows: ShopifyCouponMetrics[]): BucketVisibility {
  return {
    unpaid: rows.some((r) => unpaidOrders(r) > 0),
    roas: rows.some((r) => rowRoas(r) !== undefined),
  };
}

const roasFmt = (n: number): string => `${n.toFixed(2)}×`;
const lastOrderFmt = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
  timeZone: 'UTC',
});

function lastOrderColumn<T extends ShopifyCouponMetrics>(): ColumnDef<T> {
  return {
    id: 'last_order_at',
    header: 'Last order',
    minWidth: 104,
    align: 'right',
    cell: (r) => {
      const d = r.last_order_at ? new Date(r.last_order_at) : null;
      return d && !Number.isNaN(d.getTime()) ? (
        <span className="text-muted-foreground">{lastOrderFmt.format(d)}</span>
      ) : (
        <span className="text-muted-foreground">—</span>
      );
    },
  };
}

function roasColumns(show: BucketVisibility): ColumnDef<ShopifyCreatorCouponRow>[] {
  if (!show.roas) return [];
  return [
    {
      id: 'roas',
      header: 'ROAS',
      minWidth: 80,
      align: 'right',
      cell: (r) =>
        r.roas === undefined ? (
          <span className="text-muted-foreground">—</span>
        ) : (
          <span className="text-foreground">{roasFmt(r.roas)}</span>
        ),
    },
  ];
}

/** Awaiting payment and Partially paid in one column (amount + orders). */
function unpaidColumns<T extends ShopifyCouponMetrics>(
  fmtMoney: (n: number) => string,
  show: BucketVisibility,
): ColumnDef<T>[] {
  if (!show.unpaid) return [];
  return [
    {
      id: 'unpaid_gmv',
      header: UNPAID_LABEL,
      headerAddon: <HintIcon label={UNPAID_LABEL} hint={UNPAID_HINT} />,
      minWidth: 104,
      align: 'right',
      cell: (r) => (
        <PaymentBucketCell
          gmv={(r.awaiting_payment_gmv ?? 0) + (r.partially_paid_gmv ?? 0)}
          orders={unpaidOrders(r)}
          fmtMoney={fmtMoney}
        />
      ),
    },
  ];
}

/** With no paid orders the BE sends average_order_value 0; it shows as a muted
 *  zero amount, as Shopify Analytics does, never a dash. */
function AovCell({
  row,
  fmtMoney,
}: {
  row: ShopifyCouponMetrics;
  fmtMoney: (n: number) => string;
}) {
  if (!(row.orders > 0)) return <span className="text-muted-foreground">{fmtMoney(0)}</span>;
  return <span className="text-foreground">{fmtMoney(row.average_order_value)}</span>;
}

const spendUnavailableNote = (
  u: NonNullable<ShopifyConversionsSummary['spend_unavailable']>,
): string =>
  `Not available: spend is in ${u.spend_currency ?? 'mixed currencies'}, store sells in ${u.store_currency}`;

const SPEND_HINT =
  'Everything committed to creators up to the end of the selected range, so All time is the spend to date. ROAS divides the range\u2019s net sales by this spend, so a shorter range lowers it.';
const ROAS_HINT =
  'Net sales divided by committed spend, in the store currency. Spend counts creators accepted by the end of the selected range.';

/** Em dash with no visits: there is nothing to divide by. */
const visitRate = (orders: number, visits: number): string =>
  visits > 0 ? `${((orders / visits) * 100).toFixed(2)}%` : '—';

const SECTION_HEADING = 'mb-2 text-caption-medium uppercase tracking-wider text-muted-foreground';

const LIVE_NO_VISITS_DAYS = 7;
const DAY_MS = 86_400_000;

/** Days since the latest of going live, the code's creation and the discount start. */
function liveDaysWithoutVisits(row: ShopifyCreatorCouponRow): number | null {
  if (row.shopify_state !== 'active' || row.clicks_directional !== 0 || !row.went_live_at) {
    return null;
  }
  const since = Math.max(
    ...[row.went_live_at, row.code_created_at, row.start_date]
      .map((t) => (t ? new Date(t).getTime() : NaN))
      .filter((t) => !Number.isNaN(t)),
  );
  if (!Number.isFinite(since)) return null;
  const days = Math.floor((Date.now() - since) / DAY_MS);
  return days >= LIVE_NO_VISITS_DAYS ? days : null;
}

/** Creator identity cell for CreatorCouponTable — name/handle each get their
 *  own truncation-gated tooltip. */
function CreatorIdentityCell({ row, allTime }: { row: ShopifyCreatorCouponRow; allTime: boolean }) {
  const label = creatorLabel(row);
  const handle = row.display_name && row.handle ? row.handle.replace(/^@/, '') : null;
  const idleDays = allTime ? liveDaysWithoutVisits(row) : null;

  return (
    <div className="flex min-w-0 items-center gap-2">
      {row.avatar_url ? (
        <img src={row.avatar_url} alt="" className="size-7 shrink-0 rounded-full object-cover" />
      ) : (
        <div className="bg-muted size-7 shrink-0 rounded-full" />
      )}
      <div className="min-w-0">
        <TruncatedText as="div" text={label} className="text-foreground text-body-sm-medium" />
        {handle ? (
          <TruncatedText
            as="div"
            text={`@${handle}`}
            className="text-muted-foreground text-caption-regular"
          />
        ) : row.platform ? (
          <div className="text-muted-foreground text-caption-regular capitalize">{row.platform}</div>
        ) : null}
        {idleDays !== null ? (
          <TruncatedText
            as="div"
            text={shopifyCopy.report.liveNoVisits(idleDays)}
            className="text-muted-foreground text-caption-regular"
          />
        ) : null}
      </div>
    </div>
  );
}

/** Per-creator coupons columns (Creator identity + sales + directional clicks). */
function creatorColumns(
  fmtMoney: (n: number) => string,
  buckets: BucketVisibility,
  narrow: boolean,
  allTime: boolean,
): ColumnDef<ShopifyCreatorCouponRow>[] {
  return [
    {
      id: 'creator',
      header: 'Creator',
      // Was 320/340 (the roster-tab convention), then narrowed to 220 to
      // match the Shopify Coupons table's Creator column. Split the
      // difference: enough room for most names to fit without truncating
      // (tooltip now only opens when they actually don't — see
      // CreatorIdentityCell), while still leaving Code noticeably wider
      // than its old 120.
      minWidth: narrow ? 150 : 200,
      sticky: 'left',
      cell: (r) => <CreatorIdentityCell row={r} allTime={allTime} />,
    },
    {
      id: 'code',
      header: 'Code',
      // 140 fits a typical code; a longer one truncates and reveals on hover.
      // Pinned with Creator so the metrics scroll under both.
      minWidth: 140,
      sticky: narrow ? undefined : 'left',
      cell: (r) => <CodeCell row={r} />,
    },
    netSalesColumn<ShopifyCreatorCouponRow>(fmtMoney),
    ...unpaidColumns<ShopifyCreatorCouponRow>(fmtMoney, buckets),
    {
      id: 'average_order_value',
      header: 'AOV',
      minWidth: 84,
      align: 'right',
      sortable: true,
      cell: (r) => <AovCell row={r} fmtMoney={fmtMoney} />,
    },
    {
      id: 'orders',
      ...paidOrdersHeaderProps,
      minWidth: 96,
      align: 'right',
      sortable: true,
      cell: (r) => <CreatorOrdersCell row={r} />,
    },
    {
      id: 'refunds',
      header: 'Refunded orders',
      minWidth: 96,
      align: 'right',
      sortable: true,
      cell: (r) => <span className="text-foreground">{fmtInt(r.refunds)}</span>,
    },
    {
      id: 'clicks',
      ...visitsHeaderProps,
      minWidth: 112,
      align: 'right',
      sortable: true,
      cell: (r) => <span className="text-muted-foreground">{fmtInt(r.clicks_directional)}</span>,
    },
    ...roasColumns(buckets),
    ...sinceLiveColumns(fmtMoney),
    lastOrderColumn<ShopifyCreatorCouponRow>(),
  ];
}

/** Sales since go-live and time to first sale; blank for a creator whose content is not live. */
function sinceLiveColumns(fmtMoney: (n: number) => string): ColumnDef<ShopifyCreatorCouponRow>[] {
  const { sinceLive, firstSale } = shopifyCopy.report;
  return [
    {
      id: 'sales_since_live',
      header: sinceLive.header,
      headerAddon: <HintIcon label={sinceLive.header} hint={sinceLive.hint} />,
      minWidth: 104,
      align: 'right',
      cell: (r) =>
        r.went_live_at && r.sales_since_live !== undefined ? (
          <span className="text-foreground">{fmtMoney(r.sales_since_live)}</span>
        ) : (
          <span className="text-muted-foreground">—</span>
        ),
    },
    {
      id: 'first_sale',
      header: firstSale.header,
      headerAddon: <HintIcon label={firstSale.header} hint={firstSale.hint} />,
      minWidth: 148,
      align: 'right',
      cell: (r) =>
        r.days_to_first_sale != null ? (
          <span className="text-foreground">{firstSale.after(r.days_to_first_sale)}</span>
        ) : (
          <span className="text-muted-foreground">—</span>
        ),
    },
  ];
}

/** Campaign-wide coupons columns (shared code, no creator identity). */
function campaignColumns(
  fmtMoney: (n: number) => string,
  buckets: BucketVisibility,
  narrow: boolean,
): ColumnDef<ShopifyCampaignCouponRow>[] {
  return [
    {
      id: 'code',
      header: 'Code',
      minWidth: narrow ? 140 : 180,
      grow: true,
      sticky: 'left',
      cell: (r) => <CodeCell row={r} />,
    },
    netSalesColumn<ShopifyCampaignCouponRow>(fmtMoney),
    ...unpaidColumns<ShopifyCampaignCouponRow>(fmtMoney, buckets),
    {
      id: 'average_order_value',
      header: 'AOV',
      minWidth: 84,
      align: 'right',
      sortable: true,
      cell: (r) => <AovCell row={r} fmtMoney={fmtMoney} />,
    },
    {
      id: 'orders',
      ...paidOrdersHeaderProps,
      minWidth: 96,
      align: 'right',
      sortable: true,
      cell: (r) => <CreatorOrdersCell row={r} />,
    },
    {
      id: 'refunds',
      header: 'Refunded orders',
      minWidth: 96,
      align: 'right',
      sortable: true,
      cell: (r) => <span className="text-foreground">{fmtInt(r.refunds)}</span>,
    },
    {
      id: 'clicks',
      ...visitsHeaderProps,
      minWidth: 112,
      align: 'right',
      sortable: true,
      cell: (r) => <span className="text-muted-foreground">{fmtInt(r.clicks_directional)}</span>,
    },
    lastOrderColumn<ShopifyCampaignCouponRow>(),
  ];
}

type ConversionsView = 'active' | 'inactive';

interface SortState {
  by: ShopifyConversionSortKey | null;
  dir: 'asc' | 'desc';
}

interface ScopeTableProps<T extends ShopifyCouponMetrics> {
  campaignId: string;
  scope: ShopifyConversionScope;
  revoked: boolean;
  makeColumns: (buckets: BucketVisibility) => ColumnDef<T>[];
  range: ShopifyConversionsRange;
}

/** The same code can sit in two stores, so a row is its code plus its store. */
const rowKey = (r: ShopifyCouponMetrics): string => `${r.code}|${r.shop_domain ?? ''}`;

/** One scope's table: its own URL page/size, its own server sort, one query. */
function ScopeTable<T extends ShopifyCouponMetrics>({
  campaignId,
  scope,
  revoked,
  makeColumns,
  range,
}: ScopeTableProps<T>) {
  const url = usePagedTableUrlState(scope === 'creator' ? 'cv_creator' : 'cv_campaign');
  const [sort, setSort] = useState<SortState>({ by: null, dir: 'desc' });
  const params: ShopifyConversionsParams = {
    scope,
    revoked,
    page: url.page,
    limit: url.pageSize,
    ...range,
    ...(sort.by ? { sort_by: sort.by, sort_dir: sort.dir } : {}),
  };
  const { data, isLoading, isError, error, isPlaceholderData, isFetching, refetch, dataUpdatedAt } =
    useShopifyConversionsPage<T>(campaignId, params);
  // The cached rows stay; only this table says its refresh failed.
  const staleRows = isError && data !== undefined && !isPlaceholderData;
  useSnapPastEnd(url, data, isPlaceholderData);
  const busy = Boolean(isLoading || isPlaceholderData || isFetching);
  // Latched per view: a column seen on one page stays on every page and sort,
  // so the table doesn't change shape while paging. Resets with the view.
  const [seen, setSeen] = useState({ revoked, unpaid: false, roas: false });
  const base = seen.revoked === revoked ? seen : { revoked, unpaid: false, roas: false };
  // Placeholder rows are the previous view's; never let them decide this view.
  const visible = bucketVisibility(isPlaceholderData ? [] : (data?.entries ?? []));
  const unpaid = base.unpaid || visible.unpaid;
  const roas = base.roas || visible.roas;
  if (unpaid !== base.unpaid || roas !== base.roas || base !== seen) {
    setSeen({ revoked, unpaid, roas });
  }
  const columns = useMemo(() => makeColumns({ unpaid, roas }), [makeColumns, unpaid, roas]);

  return (
    <div aria-busy={busy}>
      <span role="status" className="sr-only">
        {busy ? 'Loading conversions…' : ''}
      </span>
      {staleRows ? (
        <TableRefreshFailedNotice
          lastLoadedAt={dataUpdatedAt ?? 0}
          retrying={isFetching}
          onRetry={() => void refetch()}
        />
      ) : null}
      <DataTable<T>
        columns={columns}
        data={data?.entries ?? []}
        getRowId={rowKey}
        ariaLabel={`${revoked ? 'Inactive' : 'Active'} ${scope === 'creator' ? 'per-creator' : 'campaign-wide'} discount code conversions`}
        className="rounded-lg"
        autoHeight
        loading={isLoading}
        error={isError && data === undefined ? (error as ApiError | null) : null}
        onRetry={() => void refetch()}
        sort={{
          by: sort.by,
          dir: sort.dir,
          onChange: (next) => {
            setSort({ by: next.by as ShopifyConversionSortKey | null, dir: next.dir });
            url.setPage(1);
          },
        }}
        pagination={{
          page: url.page,
          pageSize: url.pageSize,
          total: data?.total ?? 0,
          totalPages: data?.totalPages ?? 1,
          onPageChange: url.setPage,
          onPageSizeChange: url.setPageSize,
          pageSizeOptions: PAGE_SIZE_OPTIONS,
          maxPageSize: PAGED_TABLE_MAX_SIZE,
          noun: 'discount codes',
        }}
      />
    </div>
  );
}

/**
 * Shopify e-commerce conversions for a campaign: one server-paged table per
 * scope — per-creator coupons (coupon-attributed sales + approximate referral
 * visits, with live-resolved creator identity) and campaign-wide coupons
 * (shared codes, no creator). Current codes lead; a toggle switches both tables
 * to inactive (replaced, turned off or deleted) codes, whose historical numbers
 * stay one click away. Tiles, counts and the connection state come from the
 * summary, which covers every row, never the page on screen. Each table hides
 * itself when it has no rows; the whole section hides when Shopify isn't
 * relevant (not connected and no historical rows), so it stacks cleanly with
 * other attribution sources.
 */
export function ShopifyConversionsTab({
  campaignId,
  onConnectionChange,
  shopifyEnabled,
}: ShopifyConversionsTabProps) {
  const lane = useCampaignCapabilitiesStore((s) => s.lane);
  const accessLevel = useCampaignCapabilitiesStore((s) => s.accessLevel);
  const canTurnOn = lane === 'brand' && (accessLevel === 'write' || accessLevel === 'admin');
  // All time is the default and sends no from/to.
  const [preset, setPreset] = useState<RangePreset>('all');
  const [range, setRange] = useState(() => rangeForPreset('all'));
  const changePreset = (next: RangePreset) => {
    setPreset(next);
    if (next !== 'custom') setRange(rangeForPreset(next));
  };
  const {
    data: summary,
    isLoading,
    error,
    refetch,
    isFetching,
    dataUpdatedAt,
  } = useShopifyConversionsSummary(campaignId ?? '', !!campaignId, range, true);
  const { goToCoupons: manageCodes } = useShopifyCampaignNav();
  // Only a campaign that has coupon rows has a trend worth fetching.
  const series = useShopifyConversionsSeries(
    campaignId ?? '',
    !!campaignId && !!summary?.has_rows && !summary.data_removed,
    range,
  );
  const [view, setView] = useState<ConversionsView>('active');
  const resetPages = useResetPages(['cv_creator', 'cv_campaign']);
  const queryClient = useQueryClient();
  const [refreshing, setRefreshing] = useState(false);
  const { download, pending: exporting } = useShopifySalesExport();
  // Refetches the summary and every conversions table page of this campaign.
  const handleRefresh = () => {
    if (refreshing || !campaignId) return;
    setRefreshing(true);
    // A relative preset must roll forward past UTC midnight, or Refresh re-requests yesterday's window.
    if (preset !== 'all' && preset !== 'custom') {
      const fresh = rangeForPreset(preset);
      if (fresh.from !== range.from || fresh.to !== range.to) setRange(fresh);
    }
    void queryClient
      .refetchQueries({ queryKey: shopifyKeys.conversions(campaignId), type: 'active' })
      .catch(() => undefined)
      .finally(() => setRefreshing(false));
  };

  const fmtMoney = useMemo(() => makeMoneyFmt(summary?.currency), [summary?.currency]);
  const narrow = useBelowMd();
  const creatorCols = useCallback(
    (b: BucketVisibility) => creatorColumns(fmtMoney, b, narrow, preset === 'all'),
    [fmtMoney, narrow, preset],
  );
  const campaignCols = useCallback(
    (b: BucketVisibility) => campaignColumns(fmtMoney, b, narrow),
    [fmtMoney, narrow],
  );
  const totals = summary?.totals;
  const hasSales =
    (totals?.orders ?? 0) +
      (totals?.refunds ?? 0) +
      (totals?.awaiting_payment_orders ?? 0) +
      (totals?.partially_paid_orders ?? 0) +
      (totals?.unknown_payment_orders ?? 0) >
    0;
  const inactiveSales = useInactiveSales(
    campaignId ?? '',
    summary?.counts.creator_revoked ?? 0,
    summary?.counts.campaign_revoked ?? 0,
    range,
  );
  const hasInactiveSales = inactiveSales.gmv > 0 || inactiveSales.orders > 0;
  const summaryTiles = useMemo(() => {
    const cancelled = totals?.cancelled_orders ?? 0;
    const unknownDiscounts = totals?.discount_unknown_orders ?? 0;
    const discountsPartial = totals?.discounts_partial === true && unknownDiscounts > 0;
    const cancelledCopy = shopifyCopy.report.cancelledOrders;
    const refundSub = [
      totals?.refund_value !== undefined ? `${fmtMoney(totals.refund_value)} refunded` : null,
      cancelled > 0 ? cancelledCopy.label(cancelled) : null,
    ]
      .filter(Boolean)
      .join(' · ');
    const tiles: {
      icon: Icon;
      label: string;
      value: string;
      hint?: string;
      hintLabel?: string;
      sub?: string;
    }[] = [
      {
        icon: ShoppingBag,
        label: NET_SALES_HEADER,
        value: fmtMoney(totals?.net_gmv ?? 0),
        hint: discountsPartial
          ? `${NET_SALES_HINT} ${shopifyCopy.report.discountsPartial.hint(unknownDiscounts)}`
          : NET_SALES_HINT,
        hintLabel: discountsPartial ? shopifyCopy.report.discountsPartial.tooltipLabel : undefined,
        sub: discountsPartial
          ? shopifyCopy.report.discountsPartial.sub
          : totals?.discount_total !== undefined
            ? shopifyCopy.report.discountsGiven(fmtMoney(totals.discount_total))
            : undefined,
      },
      {
        icon: Receipt,
        label: PAID_ORDERS.label,
        value: fmtInt(totals?.orders ?? 0),
        hint: PAID_ORDERS.hint,
      },
      {
        icon: ArrowUUpLeft,
        label: 'Refunded orders',
        value: fmtInt(totals?.refunds ?? 0),
        sub: refundSub || undefined,
        hint: cancelled > 0 ? cancelledCopy.hint : undefined,
        hintLabel: cancelled > 0 ? cancelledCopy.tooltipLabel : undefined,
      },
      {
        icon: CursorClick,
        label: VISITS.label,
        value: fmtInt(totals?.clicks_directional ?? 0),
        hint: VISITS.hint,
      },
      {
        icon: Percent,
        label: VISIT_RATE.label,
        value: visitRate(totals?.orders ?? 0, totals?.clicks_directional ?? 0),
        hint: VISIT_RATE.hint,
      },
    ];
    return tiles;
  }, [totals, fmtMoney]);

  const hasRows = summary?.has_rows ?? false;
  // A 403 is an answer: this caller isn't entitled to Shopify data, so the
  // section stays silent and the other attribution sources render alone.
  // Anything else is NOT an answer, and must not be reported as "Shopify
  // isn't part of this campaign" (item 104) — the parent then showed its
  // "Connect AppsFlyer or Shopify" placeholder to a brand whose store IS
  // connected, because a failed request looked identical to no store.
  const notEntitled = error instanceof ApiError && error.httpStatus === 403;
  // `&& !summary`: a background refetch that fails (window focus, or past the
  // 60s staleTime) sets `error` while the previous summary is still in `data`.
  // Without this, a transient blip would replace good numbers with a failure
  // notice — worse than the silence this item set out to fix.
  const loadFailed = !!error && !notEntitled && !summary;
  // The cached summary stays on screen, so the failure needs its own notice.
  const refreshFailed = !!error && !notEntitled && !!summary;
  // Shopify is relevant once it's connected, has rows, or had its data removed
  // (that state is the answer, not "Shopify isn't part of this campaign"). A
  // failed load counts as relevant so the placeholder stays hidden and the
  // error below is the only thing explaining the gap.
  const dataRemoved = !!summary && (summary.data_removed || summary.store_state === 'removed');
  const dataDeleting = !!summary && !dataRemoved && isDeletingStoreData(summary);
  const relevant =
    loadFailed || dataRemoved || dataDeleting || (!!summary && (summary.connected || hasRows));

  useEffect(() => {
    if (!isLoading) onConnectionChange?.(relevant);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, relevant]);

  if (isLoading) {
    return (
      <div className="text-muted-foreground flex items-center gap-2 py-6 text-body-sm-regular">
        <LoadingSpinner /> Loading Shopify conversions…
      </div>
    );
  }
  if (loadFailed) {
    return (
      <section className="border-border bg-card rounded-lg border p-5">
        {/* Kept visible so a failed range can be abandoned for All time. */}
        <div className="mb-3">
          <RangeControl
            value={preset}
            range={range}
            onApply={(next) => {
              setRange(next);
              resetPages();
            }}
            onChange={(next) => {
              changePreset(next);
              resetPages();
            }}
          />
        </div>
        {/* The message carries role=alert, not the region, so the Retry button is not
            inside an assertive region; the section mounts on failure. */}
        <Alert
          variant="destructive"
          role={undefined}
          className="flex flex-wrap items-center gap-3 py-2"
        >
          <WarningCircle className="size-4 shrink-0" aria-hidden="true" />
          <span role="alert">{shopifyCopy.report.loadFailed}</span>
          {/* aria-disabled, not disabled: a native disabled button drops focus
              to <body> the moment it is pressed, so a keyboard user loses their
              place on the one control they just used. */}
          <Button
            variant="outline"
            size="sm"
            className={cn('rounded-full', isFetching && 'opacity-50')}
            aria-disabled={isFetching}
            onClick={() => {
              if (isFetching) return;
              void refetch();
            }}
          >
            {isFetching ? shopifyCopy.report.retrying : shopifyCopy.report.retry}
          </Button>
        </Alert>
      </section>
    );
  }
  // Not entitled to Shopify data at all → stay quiet (other sources render).
  if (notEntitled || !summary) return null;
  // Shopify simply isn't part of this campaign → render nothing.
  if (!relevant) return null;
  if (shopifyEnabled === false && !hasRows && !dataRemoved && !dataDeleting) {
    return (
      <section className="border-border bg-card rounded-lg border p-5">
        <EmptyState
          title={
            canTurnOn
              ? "Shopify isn't turned on for this campaign. Turn it on in Settings > Integrations."
              : "Shopify isn't turned on for this campaign."
          }
          className="py-2"
        />
      </section>
    );
  }
  if (dataRemoved) {
    return (
      <section className="border-border bg-card rounded-lg border p-5">
        <h3 className="text-foreground mb-4 text-body-md-semibold">Shopify sales</h3>
        <ShopifyDataRemovedNotice />
      </section>
    );
  }
  if (dataDeleting) {
    return (
      <section className="border-border bg-card rounded-lg border p-5">
        <h3 className="text-foreground mb-4 text-body-md-semibold">Shopify sales</h3>
        <Alert role="status" className="bg-muted">
          <div className="col-start-2">
            <p className="text-foreground text-body-sm-medium">
              {shopifyCopy.report.lifecycle.deleting.title}
            </p>
            <p className="text-muted-foreground mt-1 text-body-sm-regular">
              {shopifyCopy.report.lifecycle.deleting.body}
            </p>
          </div>
        </Alert>
      </section>
    );
  }

  const { counts } = summary;
  const inactiveAvailable = summary.revoked_count > 0;
  // The toggle only exists while there is something inactive to show.
  const showInactive = view === 'inactive' && inactiveAvailable;
  const creatorCount = showInactive ? counts.creator_revoked : (counts.creator_current ?? 0);
  const campaignCount = showInactive ? counts.campaign_revoked : (counts.campaign_current ?? 0);
  const activeCodeCount = (counts.creator_current ?? 0) + (counts.campaign_current ?? 0);
  const unknownOrders = totals?.unknown_payment_orders ?? 0;
  const testOrders = totals?.test_orders ?? 0;
  const linkOrders = totals?.link_orders ?? 0;
  const unavailable = unavailableStoreCopy(summary);
  const storeNote =
    unavailable?.note ??
    (summary.store_state === 'not_connected' ? shopifyCopy.state.not_connected.report : null);
  // The top of the page already explains the gap; the chart area stays neutral.
  const topNotice = refreshFailed || !!storeNote;

  return (
    <section className="flex min-w-0 flex-col gap-5" data-clarity-mask="True">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          <h3 className="text-foreground text-body-md-semibold">Shopify sales</h3>
          <InfoTooltip label={shopifyCopy.report.attributionHow.triggerLabel}>
            {shopifyCopy.report.attributionHow.body}
          </InfoTooltip>
          {unavailable ? (
            <Badge variant="outline" className="gap-1 border-amber-200 bg-amber-50 text-amber-700">
              <WarningCircle className="size-3" aria-hidden="true" /> {unavailable.badge}
            </Badge>
          ) : null}
        </div>
        <div className="ml-auto flex shrink-0 items-center gap-2">
          {campaignId && shopifyEnabled !== false ? (
            <Button variant="outline" size="sm" className="rounded-full" onClick={manageCodes}>
              {shopifyCopy.report.manageCodes}
            </Button>
          ) : null}
          <SimpleTooltip
            content={refreshing ? 'Refreshing Shopify sales' : 'Refresh Shopify sales'}
          >
            <Button
              variant="outline"
              size="icon"
              className={cn('size-9 rounded-full', refreshing && 'opacity-50')}
              aria-disabled={refreshing}
              aria-busy={refreshing}
              aria-label="Refresh Shopify sales"
              onClick={handleRefresh}
            >
              <ArrowsClockwise
                className={cn('size-4', refreshing && 'motion-safe:animate-spin')}
                aria-hidden="true"
              />
            </Button>
          </SimpleTooltip>
          {campaignId ? (
            <SimpleTooltip
              content={
                range.from || range.to
                  ? shopifyCopy.report.exportRangeHint
                  : shopifyCopy.report.exportAllHint
              }
            >
              <Button
                variant="outline"
                size="sm"
                className="rounded-full"
                aria-disabled={exporting}
                aria-busy={exporting}
                onClick={() => {
                  if (!exporting) void download(campaignId, range);
                }}
              >
                <DownloadSimple className="size-4" aria-hidden="true" />
                {exporting ? shopifyCopy.report.exporting : shopifyCopy.report.exportCsv}
              </Button>
            </SimpleTooltip>
          ) : null}
        </div>
      </div>
      {storeNote ? <p className="text-muted-foreground text-caption-regular">{storeNote}</p> : null}
      {refreshFailed ? (
        <ShopifyRefreshFailedNotice
          lastLoadedAt={dataUpdatedAt}
          retrying={isFetching}
          onRetry={() => {
            void refetch();
            void series.refetch();
          }}
        />
      ) : null}
      <ShopifySetupStrip summary={summary} helpLink={lane === 'brand'} />

      <div role="region" aria-label="Shopify sales summary">
        <div className="grid grid-cols-2 gap-5 lg:grid-cols-5">
          {summaryTiles.map((s) => (
            <KpiCard
              compact
              key={s.label}
              icon={s.icon}
              label={s.label}
              value={s.value}
              valueTooltip={s.value}
              description={s.sub}
              labelAddon={
                s.hint ? (
                  <InfoTooltip label={s.hintLabel ?? `About ${s.label}`}>{s.hint}</InfoTooltip>
                ) : undefined
              }
            />
          ))}
        </div>
      </div>
      {(summary.other_currency_orders ?? 0) > 0 ? (
        <p className="text-body-sm-regular text-muted-foreground">
          {summary.other_currency_orders === 1
            ? `1 order was placed in another currency; shown in ${summary.currency}.`
            : `${fmtInt(summary.other_currency_orders ?? 0)} orders were placed in other currencies; shown in ${summary.currency}.`}
        </p>
      ) : null}
      <div className="flex min-w-0 flex-col gap-3">
        <div className="border-border bg-card min-w-0 overflow-hidden rounded-xl border px-6 py-5">
          <div className="mb-5 flex flex-wrap items-start justify-between gap-4">
            <div className="flex flex-col gap-1">
              <h4 className="text-foreground text-body-md-semibold leading-none">
                Net sales over time
              </h4>
              <p className="text-body-sm-regular text-muted-foreground">
                Weekly paid sales after refunds · UTC
              </p>
            </div>
            <div className="flex max-w-full flex-col items-end gap-3">
              <div className="text-muted-foreground flex items-center gap-1.5 text-caption-medium">
                <span
                  className="size-2 rounded-full"
                  style={{ backgroundColor: 'var(--brand-600)' }}
                />{' '}
                Net sales
              </div>
              <RangeControl
                value={preset}
                range={range}
                onChange={(next) => {
                  changePreset(next);
                  resetPages();
                }}
                onApply={(next) => {
                  setRange(next);
                  resetPages();
                }}
              />
            </div>
          </div>
          {!topNotice && series.isError && series.data ? (
            <ShopifyRefreshFailedNotice
              lastLoadedAt={series.dataUpdatedAt ?? 0}
              retrying={series.isFetching}
              onRetry={() => void series.refetch()}
            />
          ) : null}
          {series.isLoading ? (
            <div className="text-muted-foreground flex h-[260px] items-center justify-center gap-2 text-body-sm-regular">
              <LoadingSpinner /> Loading sales trend…
            </div>
          ) : !topNotice && series.isError && !series.data ? (
            <div
              role="status"
              className="text-muted-foreground flex h-[260px] items-center justify-center gap-3 text-body-sm-regular"
            >
              Couldn't load the sales trend.
              <Button variant="outline" size="sm" onClick={() => void series.refetch()}>
                Retry trend
              </Button>
            </div>
          ) : topNotice && series.isError && !series.data ? (
            <div
              role="status"
              className="text-muted-foreground flex h-[260px] items-center justify-center text-body-sm-regular"
            >
              Trend unavailable
            </div>
          ) : (
            <SalesTrendChart buckets={series.data?.buckets ?? []} fmtMoney={fmtMoney} />
          )}
          <div className="text-muted-foreground border-border mt-4 flex flex-wrap justify-between gap-x-6 gap-y-2 border-t pt-3 text-caption-regular">
            <div className="space-y-1">
              {summary.revoked_count > 0 && campaignId ? (
                <InactiveSalesNote sum={inactiveSales.gmv} fmtMoney={fmtMoney} />
              ) : null}
              {testOrders > 0 ? (
                <p>
                  Includes {fmtMoney(totals?.test_gmv ?? 0)} from{' '}
                  {plural(testOrders, 'test order', 'test orders')}
                </p>
              ) : null}
              {linkOrders > 0 ? (
                <p>
                  Includes {plural(linkOrders, 'order', 'orders')} credited by link (no code used)
                </p>
              ) : null}
              {preset === 'all' &&
              hasRows &&
              !hasSales &&
              !hasInactiveSales &&
              summary.connected &&
              !storeNote ? (
                <p>{NO_SALES_YET}</p>
              ) : null}
              {lane === 'brand' ? (
                <p>
                  <ShopifyHelpLink anchor="shopify-metrics">
                    {shopifyCopy.report.help.metrics}
                  </ShopifyHelpLink>
                </p>
              ) : null}
              {unknownOrders > 0 ? (
                <p>
                  {plural(unknownOrders, 'order', 'orders')} with an unrecognised payment state{' '}
                  {unknownOrders === 1 ? "isn't" : "aren't"} counted.
                </p>
              ) : null}
            </div>
            <dl className="flex flex-wrap gap-x-6 gap-y-2">
              {summary.spend !== undefined ? (
                <div>
                  <dt className="flex items-center gap-1">
                    Spend to date<InfoTooltip label="About spend">{SPEND_HINT}</InfoTooltip>
                  </dt>
                  <dd className="text-foreground font-medium">{fmtMoney(summary.spend)}</dd>
                </div>
              ) : null}
              {summary.spend_unavailable ? (
                <div className="text-muted-foreground">
                  <dt>Spend / ROAS</dt>
                  <dd>{spendUnavailableNote(summary.spend_unavailable)}</dd>
                </div>
              ) : null}
              {summary.roas !== undefined ? (
                <div>
                  <dt className="flex items-center gap-1">
                    ROAS<InfoTooltip label="About ROAS">{ROAS_HINT}</InfoTooltip>
                  </dt>
                  <dd className="text-foreground font-medium">{roasFmt(summary.roas)}</dd>
                </div>
              ) : null}
              {(totals?.awaiting_payment_orders ?? 0) > 0 ? (
                <div>
                  <dt className="flex items-center gap-1">
                    {AWAITING_LABEL}
                    <InfoTooltip label={`About ${AWAITING_LABEL}`}>{AWAITING_HINT}</InfoTooltip>
                  </dt>
                  <dd className="text-foreground font-medium">
                    {fmtMoney(totals?.awaiting_payment_gmv ?? 0)} ·{' '}
                    {plural(totals?.awaiting_payment_orders ?? 0, 'order', 'orders')}
                  </dd>
                </div>
              ) : null}
              {(totals?.partially_paid_orders ?? 0) > 0 ? (
                <div>
                  <dt className="flex items-center gap-1">
                    {PARTIALLY_PAID_LABEL}
                    <InfoTooltip label={`About ${PARTIALLY_PAID_LABEL}`}>
                      {PARTIALLY_PAID_HINT}
                    </InfoTooltip>
                  </dt>
                  <dd className="text-foreground font-medium">
                    {fmtMoney(totals?.partially_paid_gmv ?? 0)} ·{' '}
                    {plural(totals?.partially_paid_orders ?? 0, 'order', 'orders')}
                  </dd>
                </div>
              ) : null}
            </dl>
          </div>
        </div>
        <ShopifyRecordedActivity
          summary={summary}
          loadedAt={dataUpdatedAt}
          className="m-0 justify-end text-right"
        />
      </div>

      {!hasRows ? (
        <p className="text-muted-foreground py-4 text-center text-body-sm-regular">
          No sales attributed yet.
          {lane === 'brand' ? (
            <>
              {' '}
              <ShopifyHelpLink anchor="shopify-codes">
                {shopifyCopy.report.help.codes}
              </ShopifyHelpLink>
            </>
          ) : null}
        </p>
      ) : (
        <div className="space-y-6">
          {inactiveAvailable ? (
            <PhaseSubTabs<ConversionsView>
              bare
              active={showInactive ? 'inactive' : 'active'}
              tabs={[
                { key: 'active', label: `Current codes (${activeCodeCount})` },
                { key: 'inactive', label: `Inactive codes (${summary.revoked_count})` },
              ]}
              onChange={(next) => {
                setView(next);
                resetPages();
              }}
            />
          ) : null}
          {showInactive ? (
            // The badge on each row says which reason applies; none of these
            // codes works any more.
            <p className="text-muted-foreground text-caption-regular">
              Replaced, deactivated or deleted codes that had orders or visits. Past orders still
              count.
            </p>
          ) : null}
          {!showInactive && activeCodeCount === 0 ? (
            <EmptyState title="No current codes. Past sales are under Inactive codes." className="py-4" />
          ) : null}
          {creatorCount > 0 && campaignId ? (
            <div>
              <h4 className={SECTION_HEADING}>Per-creator discount codes</h4>
              <ScopeTable
                campaignId={campaignId}
                scope="creator"
                revoked={showInactive}
                makeColumns={creatorCols}
                range={range}
              />
            </div>
          ) : null}
          {campaignCount > 0 && campaignId ? (
            <div>
              <h4 className={SECTION_HEADING}>Shared campaign code</h4>
              <ScopeTable
                campaignId={campaignId}
                scope="campaign"
                revoked={showInactive}
                makeColumns={campaignCols}
                range={range}
              />
            </div>
          ) : null}
        </div>
      )}
      {campaignId ? (
        <div className="mt-6">
          <ShopifyRefusedOrders campaignId={campaignId} />
        </div>
      ) : null}
    </section>
  );
}
